import { Box, Paper, Stack, Typography } from '@mui/material';
import React from 'react';
import type { RegistryApproval } from '../../api/registryApiTypes';
import ApprovalDecisionControls from './ApprovalDecisionControls';
import { approvalKindLabel, formatAmount, formatWhen } from './approvalFormat';

function ApprovalDetails({ approval }: { approval: RegistryApproval }) {
  const amount = formatAmount(approval.amount);
  return (
    <Box sx={{ minWidth: 0 }}>
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em' }}
      >
        {approvalKindLabel(approval.kind)} · requested by {approval.requestedBy.name} ·{' '}
        {formatWhen(approval.requestedAt)}
      </Typography>
      <Typography variant="h6" sx={{ mt: 0.5 }}>
        {approval.title}
      </Typography>
      {approval.summary && (
        <Typography variant="body2" color="text.secondary">
          {approval.summary}
        </Typography>
      )}
      {amount && (
        <Typography variant="body2" sx={{ mt: 1, fontWeight: 700 }}>
          {amount}
        </Typography>
      )}
      {approval.status !== 'PENDING' && (
        <Typography variant="body2" sx={{ mt: 1 }}>
          {approval.status === 'APPROVED' ? 'Approved' : 'Rejected'} by{' '}
          {approval.decidedBy?.name ?? '—'} · {formatWhen(approval.decidedAt)}
          {approval.comment ? ` · “${approval.comment}”` : ''}
        </Typography>
      )}
    </Box>
  );
}

export default function RegistryApprovalCard({
  approval,
  viewerId,
  viewerCanApprove,
  onDecide,
}: {
  approval: RegistryApproval;
  viewerId: string;
  viewerCanApprove: boolean;
  onDecide: (approve: boolean) => void;
}) {
  return (
    <Paper variant="outlined" sx={{ p: 2.5 }} data-testid={`approval-${approval.externalId}`}>
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} justifyContent="space-between">
        <ApprovalDetails approval={approval} />
        <Stack spacing={1} alignItems={{ md: 'flex-end' }} sx={{ flexShrink: 0 }}>
          <ApprovalDecisionControls
            approval={approval}
            own={approval.requestedBy.externalId === viewerId}
            viewerCanApprove={viewerCanApprove}
            onDecide={onDecide}
          />
        </Stack>
      </Stack>
    </Paper>
  );
}
